import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Paper,
  Typography,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { format, isValid, parseISO } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import RestoreIcon from '@mui/icons-material/Restore';
import LabelIcon from '@mui/icons-material/Label';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ item, onToggle, onMenuOpen, formatDueDate, formatCompletedDate, isOverdue, reorderMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !reorderMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dueDate = formatDueDate(item.dueDate);
  const completedDate = formatCompletedDate(item.completedAt);
  const overdue = isOverdue(item.dueDate, item.completed);

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={
        <IconButton
          edge="end"
          onClick={(e) => onMenuOpen(e, item.id)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      }
      sx={{
        border: '1px solid',
        borderColor: overdue ? 'error.main' : 'divider',
        borderRadius: 1,
        mb: 1,
        bgcolor: overdue ? 'error.light' : 'background.paper',
        opacity: overdue ? 0.9 : 1,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {reorderMode && (
        <ListItemIcon
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            touchAction: 'none',
            minWidth: 36,
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
        </ListItemIcon>
      )}
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Checkbox
          edge="start"
          checked={item.completed}
          onChange={() => onToggle(item.id)}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography
              sx={{
                textDecoration: item.completed ? 'line-through' : 'none',
                opacity: item.completed ? 0.6 : 1,
              }}
            >
              {item.text}
            </Typography>
            {item.notes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.85rem',
                  fontStyle: 'italic',
                  opacity: item.completed ? 0.5 : 0.8
                }}
              >
                {item.notes}
              </Typography>
            )}
            {(item.category || dueDate || completedDate) && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  {item.category && (
                    <Chip
                      icon={<LabelIcon />}
                      label={item.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  )}
                  {dueDate && (
                    <Chip
                      icon={<EventIcon />}
                      label={dueDate}
                      size="small"
                      color={overdue ? 'error' : 'default'}
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
                {completedDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem', opacity: 0.6, whiteSpace: 'nowrap' }}
                  >
                    Done {completedDate}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

const TodoListEnhanced = ({ list, onUpdateList }) => {
  const [newTodo, setNewTodo] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newDueDate, setNewDueDate] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDueDate, setEditDueDate] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);

  // Categories management
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [categoryDraftList, setCategoryDraftList] = useState([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');

  const categories = list.categories || [];

  // Sensors for drag and drop (pointer, touch, keyboard)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  console.log('TodoList received list:', list);

  const handleAddTodo = async () => {
    if (!newTodo.trim()) return;

    console.log('Adding todo:', newTodo);
    const newItem = {
      id: Date.now().toString(),
      text: newTodo.trim(),
      completed: false,
      notes: newNotes.trim(),
      dueDate: newDueDate ? newDueDate.toISOString() : null,
      category: newCategory || null,
      createdAt: new Date().toISOString()
    };

    // Add new item at the beginning of incomplete items
    const currentItems = list.items || [];
    const incomplete = currentItems.filter(item => !item.completed);
    const complete = currentItems.filter(item => item.completed);
    const updatedItems = [newItem, ...incomplete, ...complete];
    console.log('Updated items:', updatedItems);

    try {
      await onUpdateList(list.id, { items: updatedItems });
      console.log('Todo added successfully');
      setNewTodo('');
      setNewNotes('');
      setNewDueDate(null);
      setNewCategory('');
    } catch (error) {
      console.error('Error adding todo:', error);
      alert('Failed to add todo: ' + error.message);
    }
  };

  const handleToggleTodo = async (itemId) => {
    const updatedItems = list.items.map(item => {
      if (item.id === itemId) {
        const newCompleted = !item.completed;
        return {
          ...item,
          completed: newCompleted,
          completedAt: newCompleted ? new Date().toISOString() : null
        };
      }
      return item;
    });

    // Sort items: incomplete first, completed last
    const incomplete = updatedItems.filter(item => !item.completed);
    const complete = updatedItems.filter(item => item.completed);
    const sortedItems = [...incomplete, ...complete];

    await onUpdateList(list.id, { items: sortedItems });
  };

  const handleDeleteTodo = async (itemId) => {
    const updatedItems = list.items.filter(item => item.id !== itemId);
    await onUpdateList(list.id, { items: updatedItems });
    handleMenuClose();
  };

  const handleEditOpen = (item) => {
    setEditingItem(item);
    setEditText(item.text);
    setEditNotes(item.notes || '');
    setEditDueDate(item.dueDate ? parseISO(item.dueDate) : null);
    setEditCategory(item.category || '');
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (!editText.trim()) return;

    const updatedItems = list.items.map(item =>
      item.id === editingItem.id
        ? {
            ...item,
            text: editText.trim(),
            notes: editNotes.trim(),
            dueDate: editDueDate ? editDueDate.toISOString() : null,
            category: editCategory || null,
          }
        : item
    );
    await onUpdateList(list.id, { items: updatedItems });
    setEditDialogOpen(false);
    setEditingItem(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddTodo();
    }
  };

  const handleMenuOpen = (event, itemId) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveItemId(itemId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveItemId(null);
  };

  // Categories dialog handlers
  const handleOpenCategoriesDialog = () => {
    setCategoryDraftList([...categories]);
    setNewCategoryInput('');
    setCategoriesDialogOpen(true);
  };

  const handleAddCategoryToDraft = () => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed || categoryDraftList.includes(trimmed)) return;
    setCategoryDraftList([...categoryDraftList, trimmed]);
    setNewCategoryInput('');
  };

  const handleRemoveCategoryFromDraft = (cat) => {
    setCategoryDraftList(categoryDraftList.filter(c => c !== cat));
  };

  const handleSaveCategories = async () => {
    await onUpdateList(list.id, { categories: categoryDraftList });
    setCategoriesDialogOpen(false);
  };

  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return null;
      return format(date, 'dd/MM/yyyy');
    } catch (error) {
      return null;
    }
  };

  const formatCompletedDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return null;
      return format(date, 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return null;
    }
  };

  const isOverdue = (dateString, completed) => {
    if (!dateString || completed) return false;
    try {
      const dueDate = parseISO(dateString);
      if (!isValid(dueDate)) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDate < today;
    } catch (error) {
      return false;
    }
  };

  // Handle drag end event
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const items = list.items || [];
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      await onUpdateList(list.id, { items: reorderedItems });
    }
  };

  // Reset order - order incomplete tasks by due date, then completed tasks at bottom
  const handleResetOrder = async () => {
    const items = list.items || [];
    const incomplete = items.filter(item => !item.completed);
    const complete = items.filter(item => item.completed);

    // Sort incomplete by due date (null dates go to the end)
    incomplete.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });

    const resetItems = [...incomplete, ...complete];
    await onUpdateList(list.id, { items: resetItems });
  };

  // Use items in their stored order (user can manually reorder)
  const items = list.items || [];

  const activeItem = activeItemId ? items.find(i => i.id === activeItemId) : null;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            placeholder="Add new to-do item..."
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            size="medium"
            InputProps={{
              endAdornment: newTodo && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear input"
                    onClick={() => setNewTodo('')}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ mt: 1 }}
            size="small"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Button
            variant="contained"
            onClick={handleAddTodo}
            disabled={!newTodo.trim()}
            sx={{
              minWidth: { xs: '56px', sm: 'auto' },
              px: { xs: 1, sm: 3 },
              height: '56px'
            }}
          >
            <AddIcon />
          </Button>
        </Box>

        {expanded && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              variant="outlined"
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={handleOpenCategoriesDialog}
                title="Edit categories"
                sx={{ flexShrink: 0 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Due Date"
                value={newDueDate}
                onChange={setNewDueDate}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                  actionBar: {
                    actions: ['clear', 'today']
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        )}
      </Box>

      <Box sx={{ mb: 0.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant={reorderMode ? 'contained' : 'outlined'}
          size="small"
          startIcon={<SwapVertIcon />}
          onClick={() => setReorderMode(!reorderMode)}
        >
          {reorderMode ? 'Done' : 'Reorder'}
        </Button>
        {reorderMode && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreIcon />}
            onClick={handleResetOrder}
          >
            Reset Order
          </Button>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <List sx={{ width: '100%' }}>
            {items.length > 0 ? (
              items.map((item) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  onToggle={handleToggleTodo}
                  onMenuOpen={handleMenuOpen}
                  formatDueDate={formatDueDate}
                  formatCompletedDate={formatCompletedDate}
                  isOverdue={isOverdue}
                  reorderMode={reorderMode}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No items yet. Add your first to-do!
              </Typography>
            )}
          </List>
        </SortableContext>
      </DndContext>

      {/* Menu for mobile actions */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => activeItem && handleEditOpen(activeItem)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Edit</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteTodo(activeItemId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit To-Do</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task"
            fullWidth
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleEditSave();
              }
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={handleOpenCategoriesDialog}
              title="Edit categories"
              sx={{ flexShrink: 0 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <DatePicker
              label="Due Date"
              value={editDueDate}
              onChange={setEditDueDate}
              format="dd/MM/yyyy"
              slotProps={{
                textField: { fullWidth: true, margin: 'dense' },
                actionBar: {
                  actions: ['clear', 'today']
                }
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Categories Dialog */}
      <Dialog open={categoriesDialogOpen} onClose={() => setCategoriesDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit Categories</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <TextField
              label="New category"
              size="small"
              fullWidth
              value={newCategoryInput}
              onChange={(e) => setNewCategoryInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddCategoryToDraft();
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddCategoryToDraft}
              disabled={!newCategoryInput.trim() || categoryDraftList.includes(newCategoryInput.trim())}
              sx={{ flexShrink: 0 }}
            >
              <AddIcon />
            </Button>
          </Box>
          {categoryDraftList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No categories yet. Add one above.
            </Typography>
          ) : (
            <List dense>
              {categoryDraftList.map((cat) => (
                <ListItem
                  key={cat}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => handleRemoveCategoryFromDraft(cat)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LabelIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={cat} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoriesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCategories} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TodoListEnhanced;
